import { useState, useEffect, useCallback } from 'react';
import useTokenStore from '../store/store';
import { useSavedSearchStore } from '../store/savedSearchStore';
import { getSavedSearchMatches, markMatchesSeen } from '../utils/savedSearches';
import { SavedSearchMatch } from '../types/savedSearch';

const MATCHES_PAGE_SIZE = 20;

// The buyer's new-property match feed. Loads page 1 on mount, appends on
// loadMore, and marks everything seen once the first page has rendered (the
// panel itself is the read receipt). Cancellation-guarded like
// useBuyerReservations.
export const useSavedSearchMatches = ({ markSeenOnLoad = true } = {}) => {
  const [matches, setMatches] = useState<SavedSearchMatch[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const getToken = useTokenStore((state) => state.token);

  useEffect(() => {
    setMatches([]);
    setTotal(0);
    setPage(1);
    setLoading(false);
    if (!getToken) return;
    let active = true;
    setLoading(true);
    getSavedSearchMatches(getToken, 1, MATCHES_PAGE_SIZE)
      .then((result) => {
        if (!active) return;
        setMatches(result.matches);
        setTotal(result.total);
        if (markSeenOnLoad && result.ok) {
          // Fire-and-forget: a failed mark-seen just means the badge
          // survives one more visit. The store badge clears immediately —
          // the buyer is looking at the panel right now.
          markMatchesSeen(getToken);
          useSavedSearchStore.getState().clearUnseen();
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [getToken, markSeenOnLoad]);

  const loadMore = useCallback(async () => {
    if (!getToken || loading) return;
    const nextPage = page + 1;
    setLoading(true);
    try {
      const result = await getSavedSearchMatches(getToken, nextPage, MATCHES_PAGE_SIZE);
      // A failed page must not advance pagination or clobber total — the
      // fail-closed { total: 0 } would hide "Load more" and block retry.
      if (!result.ok) return;
      setMatches((current) => [...current, ...result.matches]);
      setTotal(result.total);
      setPage(nextPage);
    } finally {
      setLoading(false);
    }
  }, [getToken, loading, page]);

  const hasMore = matches.length < total;

  return { matches, total, loading, loadMore, hasMore };
};
